import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserDataContext } from "../components/UserDataContext";
import { Container } from "react-bootstrap";

import {
  Button,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Stack,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiRequest } from "../components/Config";
import Alerts from "../components/Alerts";

export default function Menu() {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [options, setOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loadingData, setLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  useEffect(() => {
    if (userData && Number(userData.group) !== 3) {
      navigate("/nopermission");
    }
  }, [userData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      const optionsResponse = await apiRequest("getpossibilities", "GET");
      if (Array.isArray(optionsResponse)) setOptions(optionsResponse);
    };

    if (userData?.userId) fetchData();
  }, [userData]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!userData?.userId) return;

      setLoadingData(true);

      try {
        const response = await apiRequest(
          `get-user-possibilities/${userData.userId}`,
          "GET",
        );

        if (response.success && Array.isArray(response.selectedIds)) {
          setSelectedItems(new Set(response.selectedIds.map(Number)));
        }
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserSelections();
  }, [userData]);

  const groups = useMemo(() => {
    const grouped = options.reduce((acc, item) => {
      if (!acc[item.groupname]) acc[item.groupname] = [];
      acc[item.groupname].push(item);
      return acc;
    }, {});
    return Object.entries(grouped);
  }, [options]);

  const toggleItem = (id) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const payload = {
        userId: userData.userId,
        selectedIds: Array.from(selectedItems),
      };

      const response = await apiRequest("save-possibilities", "POST", payload);

      setApiResponse(
        response.success
          ? {
              title: "Sukces",
              severity: "success",
              info: "Dane zapisane.",
            }
          : {
              title: "Błąd",
              severity: "warning",
              info: "Nie udało się zapisać.",
            },
      );

      setShowApiResponse(true);
    } finally {
      setIsSaving(false);
    }
  };

  const currentGroup = groups[currentPage];
  const hasNextPage = currentPage < groups.length - 1;
  const hasPrevPage = currentPage > 0;

  const progress = ((currentPage + 1) / groups.length) * 100;

  return (
    <Container style={{ marginTop: "40px" }}>
      {showApiResponse && <Alerts data={apiResponse} />}

      <Typography variant="h4" mb={1}>
        Edycja profilu
      </Typography>

      <Typography mb={3} color="text.secondary">
        {userData?.fullName}, {userData?.cityName}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ mb: 4, height: 8, borderRadius: 4 }}
      />

      <Card sx={{ borderRadius: 4 }}>
        <CardContent>
          {loadingData ? (
            <Box textAlign="center" py={6}>
              <CircularProgress />
              <Typography mt={2}>Ładowanie...</Typography>
            </Box>
          ) : currentGroup ? (
            <>
              <Typography variant="h5" mb={3}>
                {currentGroup[0]}
              </Typography>

              <Grid container spacing={2}>
                {currentGroup[1].map((item) => {
                  const selected = selectedItems.has(item.id);

                  return (
                    <Grid item xs={12} md={6} key={item.id}>
                      <Card
                        onClick={() => toggleItem(item.id)}
                        sx={{
                          cursor: "pointer",
                          border: selected
                            ? "2px solid #1976d2"
                            : "1px solid #ddd",
                          backgroundColor: selected
                            ? "rgba(25,118,210,0.08)"
                            : "white",
                          transition: "0.2s",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <CheckCircleIcon
                            color={selected ? "primary" : "disabled"}
                          />

                          <Typography>{item.activity}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Stack direction="row" spacing={2} mt={4}>
                <Button
                  variant="outlined"
                  disabled={!hasPrevPage}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Wstecz
                </Button>

                {hasNextPage ? (
                  <Button
                    variant="contained"
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Dalej
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Zapisz zmiany"
                    )}
                  </Button>
                )}
              </Stack>
            </>
          ) : (
            <Typography>Brak danych</Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
